import { Button } from "@secure-vault/ui";
import { motion } from "framer-motion";
import { Check, KeyRound, Lock, Palette, Shield, Users } from "lucide-react";

import { SeatsPlan, selectedPlan, type Plan } from "@/config/constants";
import { appUrl } from "@/lib/app-links";

interface PriceAmountProps {
  plan: Plan;
  priceSuffix: string;
}

export function PriceAmount({ plan, priceSuffix }: PriceAmountProps) {
  return (
    <div className="mt-4 flex flex-col">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        key={plan.id}
        className="flex items-baseline"
      >
        <span className="font-display text-foreground text-5xl font-bold tracking-tight">
          {plan.price.replace(/\.00$/, "")}
        </span>
        <span className="text-muted-foreground ml-2 text-lg font-medium">
          {SeatsPlan.currency} {priceSuffix}
        </span>
      </motion.div>
    </div>
  );
}

export function PricingPageHeader() {
  return (
    <div className="mb-12 text-center">
      <h2 className="font-display text-foreground text-3xl font-bold tracking-tight sm:text-4xl">
        Simple pricing. No client limits.
      </h2>
      <p className="text-muted-foreground mx-auto mt-4 max-w-2xl text-lg">
        One annual plan with every core capability included. Add as many client
        vaults as your practice needs, with no per-client fees.
      </p>
    </div>
  );
}

export function Pricing() {
  const features = [
    {
      icon: <Lock className="text-primary h-5 w-5" />,
      title: "Browser-Side Encryption",
      description:
        "File contents and vault keys are protected on your client's device before upload.",
    },
    {
      icon: <Palette className="text-primary h-5 w-5" />,
      title: "Fully Branded Experience",
      description:
        "Your practice name, logo, colours and dedicated portal carry through the client and trustee journey.",
    },
    {
      icon: <Users className="text-primary h-5 w-5" />,
      title: "Unlimited Client Vaults",
      description:
        "Create as many client vaults as your practice needs. You only pay for adviser seats.",
    },
    {
      icon: <KeyRound className="text-primary h-5 w-5" />,
      title: "Trustee Safeguards",
      description:
        "Threshold key sharing helps ensure no single trustee can access the vault alone.",
    },
    {
      icon: <Shield className="text-primary h-5 w-5" />,
      title: "Adviser-Controlled Release",
      description:
        "Trustee access begins only when your practice initiates the secure handover.",
    },
  ];

  return (
    <section id="pricing" className="border-border bg-muted/10 border-t py-20">
      <div className="container mx-auto max-w-6xl px-6">
        <PricingPageHeader />

        <div className="border-border bg-card mx-auto flex max-w-5xl flex-col overflow-hidden rounded-2xl border shadow-[var(--ds-shadow-lg)] lg:flex-row">
          {/* Left Side - Details & Call To Action */}
          <div className="flex flex-col justify-between p-8 lg:w-1/2 lg:p-12">
            <div>
              <span className="text-primary font-display text-xs font-bold font-semibold tracking-wider uppercase">
                Professional Adviser Plan
              </span>
              <h3 className="font-display text-foreground mt-2 text-3xl font-bold">
                Complete Practice Access
              </h3>
              <p className="text-muted-foreground mt-4">
                Built for independent advisers and growing practices. One annual
                subscription, with the freedom to cancel renewal at any time.
              </p>

              <div className="border-border mt-8 border-t pt-6">
                <PriceAmount
                  plan={selectedPlan}
                  priceSuffix={selectedPlan.priceSuffix}
                />
                <p className="text-primary mt-2 text-sm font-semibold">
                  {SeatsPlan.baseSeats} adviser seats included, then{" "}
                  {SeatsPlan.extraSeatBlockPrice.replace(/\.00$/, "")} / year
                  for each additional {SeatsPlan.extraSeatBlockSize}-seat block
                </p>
              </div>
            </div>

            <div className="mt-8">
              <Button
                className="bg-primary text-primary-foreground hover:bg-primary-hover w-full cursor-pointer py-6 text-base font-medium shadow-[var(--ds-shadow-brand)] transition-colors"
                asChild
              >
                <a href={appUrl("/register")}>Create Your Free Account</a>
              </Button>
              <p className="text-muted-foreground mt-3 text-center text-xs">
                Explore Secure Legacy for free. Subscribe when you are ready to
                create your first client vault.
              </p>
            </div>
          </div>

          {/* Right Side - Included Features */}
          <div className="bg-[var(--ds-color-secondary-500)] p-8 text-white lg:w-1/2 lg:p-12">
            <h4 className="font-display text-lg font-semibold text-white">
              Everything Included:
            </h4>

            <ul className="mt-8 space-y-5">
              {features.map((feature) => (
                <li key={feature.title} className="flex items-start">
                  <div className="mt-0.5 mr-4 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-white/10">
                    <Check className="text-primary h-4 w-4" />
                  </div>
                  <div>
                    <h5 className="font-display font-semibold text-white">
                      {feature.title}
                    </h5>
                    <p className="mt-1 text-sm text-zinc-300">
                      {feature.description}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
